import React, { useMemo, useReducer, useState } from 'react'
import type { FormProps } from 'react-final-form'
import { Form } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import type { FormApi } from 'final-form'
import find from 'lodash/find'
import omitBy from 'lodash/omitBy'
import { openModal } from '@/components/composed/Modal/actions'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import { Breadcrumbs, Loader, MetaInfo, PageNoticeText } from '@/components/ui'
import { isUserAgrarian } from '@/logic/auth/reducer'
import { TemplatesFilterName } from '@/logic/harvestSale/constants'
import asyncEpics from '@/logic/harvestSale/epics'
import {
  getFilters,
  getIndicators,
  getPaginationInfo,
  getTemplates,
  isFetchingTemplatesFailed,
  isTemplatesFetching,
} from '@/logic/harvestSale/reducer'
import { fetchTemplates } from '@/logic/harvestSale/thunks'
import { pushGtmEventOnBlockHarvestSaleCultureLotLinkClick } from '@/logic/metrika/harvest/helpers'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { RequestHarvestSaleByTemplateModal } from '@/pages/HarvestSaleLots/components'
import type { IRequestHarvestSaleByTemplateModalOptions } from '@/pages/HarvestSaleLots/components/RequestHarvestSaleByTemplateModal/interfaces'
import { HarvestSaleLotsContext } from '@/pages/HarvestSaleLots/constants'
import type { IHarvestSaleLotsContext } from '@/pages/HarvestSaleLots/interfaces'
import type { RootState } from '@/redux/interfaces'
import { addNewEpicsLazily } from '@/utils/addNewEpicsLazily'
import { useMount } from '@/utils/hooks'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { getHarvestSaleLotsUrl } from '@/utils/outputs/url'
import { useHarvestSaleLotsFormApi } from '@/utils/outputs/useHarvestSaleLotsFormApi'
import { useHarvestSaleLotsPageParams } from '@/utils/outputs/useHarvestSaleLotsPageParams'
import { CultureSaleRequest, NoBuyersModal, Templates } from './components'
import { cultureSaleBreadcrumbs, templatesPerPage } from './constants'
import { messages } from './messages'
import './styles.scss'

addModalToWellKnown({ RequestHarvestSaleByTemplateModal, NoBuyersModal })
addNewEpicsLazily([asyncEpics])

const mapState = (state: RootState) => ({
  isFetching: isTemplatesFetching()(state),
  indicators: getIndicators()(state),
  templates: getTemplates()(state),
  isFailure: isFetchingTemplatesFailed()(state),
  paginationInfo: getPaginationInfo()(state),
  allFilters: getFilters()(state).all,
  isAgrarian: isUserAgrarian()(state),
  userGtmInfo: getUserGtmInfo()(state),
})

const CulturesSalesLots: React.FC = () => {
  const dispatch = useDispatch()
  const { cultureId } = useHarvestSaleLotsPageParams()

  const { templates = [], isFetching, isFailure, paginationInfo, allFilters, indicators, isAgrarian, userGtmInfo } = useSelector(mapState)
  const { userRegion } = useUserRegion()

  const { isDesktop, isMobile } = useDeviceType()

  const { submitFilterForm, resetFilterForm, saveFilterValues, savedFilterValues } = useHarvestSaleLotsFormApi(templatesPerPage)
  const [paginationKey, setPaginationKey] = useState<number>(0)
  const [dropdownKey, setDropdownKey] = useReducer(prevState => prevState + 1, 0)

  const { harvestSaleLotsBreadcrumbs, cultureName } = useMemo(() => {
    const foundCultureName = find(find(allFilters, { value: 'cultures' })?.options, { value: cultureId })?.name

    return { harvestSaleLotsBreadcrumbs: [...cultureSaleBreadcrumbs, { caption: foundCultureName }], cultureName: foundCultureName }
  }, [allFilters, cultureId])

  useMount(() => {
    dispatch(
      fetchTemplates({
        itemsPerPage: templatesPerPage,
        pageNumber: undefined,
        indicators,
        filterValues: {
          cultureId,
          sortBy: undefined,
        },
      }),
    )
  })

  const openRequestingHarvestSaleModal = (contentOptions: IRequestHarvestSaleByTemplateModalOptions) =>
    dispatch(
      openModal({
        options: {
          dialogId: 'RequestHarvestSaleByTemplateModal',
        },
        contentOptions,
      }),
    )

  if (isFailure) return <PageNoticeText>{messages.culturesSaleError}</PageNoticeText>

  return (
    <section className="page-wrapper page-wrapper_no-mobile page-wrapper_no-tablet">
      <MetaInfo metaInfo={{ title: cultureName }} />
      <div className="page-wrapper_tablet page-wrapper_mobile">
        <Breadcrumbs customBreadcrumbs={harvestSaleLotsBreadcrumbs} isMobile={isMobile} />
        <div className="row row-align_base row-justify_between">
          <h2 className="space-holder48-from-md space-holder32-only-md space-holder16-sm space-holder-top24-from-sm">{cultureName}</h2>
        </div>
      </div>
      <div className="culture-sale-page">
        <Loader isVisible={isFetching} />
        <div className="space-holder16 space-holder24-from-md">
          <CultureSaleRequest isAgrarian={isAgrarian} />
        </div>

        <Form onSubmit={submitFilterForm} initialValues={savedFilterValues}>
          {({ form, values, initialValues }: FormProps) => {
            const onChangeFilter = () =>
              setTimeout(() => {
                setPaginationKey(paginationKey + 1)
                // eslint-disable-next-line padding-line-between-statements
                ;(form as FormApi).submit()
              }, 100)

            const cultureValues = values[TemplatesFilterName.CULTURES]

            const selectedCultureId = Array.isArray(cultureValues)
              ? cultureValues.toString()
              : Object.keys(omitBy(cultureValues, value => !value))?.toString()

            const context: IHarvestSaleLotsContext = {
              resetFilter: () => {
                setDropdownKey()
                form?.reset(initialValues)
                resetFilterForm()
              },
              onChangeFilter,
              selectedCultureId,
              initialValues,
              dropdownKey,
              setDropdownKey,
              formValues: values,
              saveFilterValues,
              savedFilterValues,
            }

            return (
              <HarvestSaleLotsContext.Provider value={context}>
                {templates.length > 0 && (
                  <div className="culture-sale-page-templates-full-wrapper">
                    <Templates
                      templates={templates}
                      openRequestingHarvestSaleModal={openRequestingHarvestSaleModal}
                      paginationInfoTotal={paginationInfo.total}
                    />
                    {paginationInfo.total > templatesPerPage && isDesktop && (
                      <CommonLink
                        url={getHarvestSaleLotsUrl(cultureId)}
                        className="culture-sale-page-show-all-offers button button_outline"
                        onClick={() => pushGtmEventOnBlockHarvestSaleCultureLotLinkClick(userGtmInfo, userRegion.name, cultureId)}
                      >
                        <div>{messages.culturesSaleShowAllOffersButton}</div>
                      </CommonLink>
                    )}
                  </div>
                )}
              </HarvestSaleLotsContext.Provider>
            )
          }}
        </Form>
      </div>
    </section>
  )
}

export default CulturesSalesLots
